import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import type { RelatedDisplayItem } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import type { AnyItem, GlobalCollectionId } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { usePageDisplayMode } from '#layers/blocks/composables/block-context/use-page-display-mode';
import { getRelatedItemDetailRoute } from '#layers/blocks/utils/relation/related-item-detail-route';
import { getCollectionPrimaryKeyFields, useCollection } from '#layers/data-model/public';
import { useCollectionRoutes } from '#layers/spaces/public';

/**
 * Detail routes of the related rows whose record can be opened, keyed by row object because row
 * ids collide when the junction has no primary key. Pending creations have no record yet, a
 * collection without a detail page yields nothing, and neither does a form shown in a drawer or
 * modal: the page underneath would navigate while the overlay stays open on top of it.
 */
export function useRelatedItemRoutes<T extends AnyItem>(
	items: MaybeRefOrGetter<RelatedDisplayItem<T>[]>,
	collectionId: MaybeRefOrGetter<GlobalCollectionId>,
): ComputedRef<Map<RelatedDisplayItem<T>, RouteLocationRaw>> {
	const { collection } = useCollection(collectionId);
	const { detailRoute } = useCollectionRoutes(collection);
	const pageDisplayMode = usePageDisplayMode();
	const primaryKeyFields = computed(() => collection.value ? getCollectionPrimaryKeyFields(collection.value) : []);

	return computed(() => {
		const routes = new Map<RelatedDisplayItem<T>, RouteLocationRaw>();

		if (pageDisplayMode.value || primaryKeyFields.value.length === 0) return routes;

		for (const item of toValue(items)) {
			if (!item.persisted) continue;

			const route = getRelatedItemDetailRoute(item.data, primaryKeyFields.value, detailRoute);
			if (route) routes.set(item, route);
		}

		return routes;
	});
}
